import { buildCollection } from "@firecms/core";
import { products } from "@one-ai/shared/cms/schema";
import type { ContentStream } from "@one-ai/shared/cms/streams";
import { SITES } from "@one-ai/shared/org/sites";
import { NewArticleAction } from "../components/NewArticleAction";
import { RebuildActions } from "../components/RebuildActions";
import { ImageUploadField } from "../fields/ImageUploadField";
import { PlainTextAreaField } from "../fields/PlainTextAreaField";
import { RichTextField } from "../fields/RichTextField";
import { StatusSwitchField } from "../fields/StatusSwitchField";
import { TagChipsField } from "../fields/TagChipsField";
import {
    type CollectionStrings,
    currentCmsLocale,
} from "../i18n/collection-strings";
import type { ArticleCms } from "../lib/nextSlug";
import { onSaveRebuild } from "../lib/onSaveRebuild";
import { assertNoRetiredTags } from "../lib/retiredTags";
import { todayIso } from "./today";

/**
 * The CMS-side shape of a news article.
 *
 * This is the one place in the repo where the editor type and the parsed type
 * deliberately differ, and both divergences are load-bearing:
 *
 * - **`updatedAt` is a Firestore timestamp.** FireCMS's `autoValue` writes a
 *   `Date`; `unwrapValue` reads it back as an ISO string through its
 *   `timestampValue` branch, which is what `Article.updatedAt` describes.
 * - **`tags` is an array of document references,** which is what gives editors
 *   a real picker instead of free-typed slugs. Because `tagCollection` sets
 *   `customId: true`, `unwrapValue`'s `referenceValue` branch turns each one
 *   into its document id — the slug the article means to store.
 *
 * Every other field is `Article` verbatim, and `PropertiesOrBuilders` is a
 * required mapped type, so renaming a schema field still breaks this build.
 *
 * It lives in `lib/nextSlug.ts` because the draft record that file builds has
 * to be typed against it, and importing it from here would make that a cycle:
 * this file imports `NewArticleAction`, which imports that.
 */

/**
 * One AI news. Unlike the two OneLive collections, these records own their page
 * — `/news/[slug]`, prerendered per locale — rather than linking out.
 *
 * **A factory, not a const, because every label is localised.** FireCMS types
 * collection and property `name` / `description` as plain strings and hands
 * property builders no locale, so the only way to translate them is to build
 * the whole definition for a known locale. `App.tsx` does that inside
 * `FireCMSi18nProvider`, where the active locale is readable.
 *
 * See `apps/one-ai/src/spec/publish-and-rebuild.md`.
 */
export const articleCollection = (
    stream: ContentStream,
    { article: t, streams, upload, richText, tagPicker }: CollectionStrings,
) => {
    /**
     * The product label is news-only (`docs/news-product-category.md`), so
     * both halves — the property and its `propertiesOrder` entry — are gated
     * on the stream, and the blog editor stays exactly as it was. Gating the
     * property too, not just the order entry, matters: `useColumnIds` appends
     * any property `propertiesOrder` omits, so a blog listing would still grow
     * an always-empty column.
     */
    const hasProduct = stream.key === "news";

    return buildCollection<ArticleCms>({
        id: stream.collection,
        path: stream.collection,
        name: streams[stream.key].article.collection.name,
        singularName: streams[stream.key].article.collection.singularName,
        icon: "Feed",
        description: streams[stream.key].article.collection.description,
        /**
         * An article is a page, so editing one should be a page too — and the
         * side panel gave a two-locale rich text body about a third of the
         * window. `full_screen` also puts the record on its own URL, which is
         * shareable; the collection table's own default is `side_panel`, so
         * this has to be set explicitly.
         */
        openEntityMode: "full_screen",
        /**
         * The document id IS the slug, so that URL reads `/c/news/12` rather
         * than `/c/news/a7Kf9x2Qm`. Same reasoning as `tagCollection`, with
         * one extra consequence: `slug` is still a stored field — the public
         * site and `ArticleSchema` both read it — so it is written
         * alongside the id rather than the editor typing it twice.
         *
         * Since 2026-09-07 that id is **assigned, not typed**: `NewArticleAction`
         * computes the next number. `customId` stays `true` anyway — it is what
         * makes the id the slug, and it is still what a manually-reached
         * `#new` form would demand.
         */
        customId: true,
        /**
         * **`create: false` is what hides FireCMS's own Add button**, leaving
         * `NewArticleAction` as the only way in — the "numbered only" decision
         * of 2026-09-07. Nothing else is restricted: editing and deleting are
         * FireCMS's defaults, spelled out here so the block reads as a policy
         * rather than a switch someone flipped.
         *
         * Two things follow, both wanted. The action writes through
         * `context.dataSource`, which is the raw data source and honours no
         * permission — so suppressing the button does not disarm the button
         * that replaces it. And `canCreateEntity` also gates the **Copy entity**
         * action (see `EntityFormActions`), which is the one other path that
         * would ask an editor to type a document id, and would ask it while
         * copying a slug that must not be reused.
         */
        permissions: { read: true, create: false, edit: true, delete: true },
        callbacks: {
            /**
             * Refuses a retired tag, keeps `slug` equal to the document id, and
             * rejects an id that the read path would drop.
             *
             * **The `previousValues` guard is not optional.** Articles created
             * before `customId` have a random id and a correct slug; without
             * the guard, opening one and saving would overwrite its slug with
             * that random id and 404 a live page. They keep their id-based URL
             * until someone recreates them, which is the only safe answer —
             * Firestore cannot rename a document.
             *
             * FireCMS cannot validate a custom id declaratively (`customId` is
             * `boolean | EnumValues | "optional"`, no regex) and an id cannot
             * be edited afterwards, so throwing here is what stops a typo from
             * becoming a permanent URL. See `tagCollection` for the same rule.
             *
             * **A backstop rather than the normal path since 2026-09-07.**
             * `NewArticleAction` writes both the id and a matching `slug`
             * before the form opens, so an editor's first save already has
             * `previousValues` and returns on the line below. What is left here
             * is the create form reached by hand at `/c/news#new` — which
             * `permissions.create: false` hides but does not make unreachable
             * — and that is exactly the case where a typed id still needs
             * validating.
             */
            onPreSave: async ({ entityId, values, previousValues, context }) => {
                /**
                 * The half of "a retired tag cannot be assigned" that does not
                 * depend on which UI path the editor took. The other half is
                 * the `forceFilter` on the `tags` property below, which a text
                 * search suspends (see `lib/searchFilter.ts`) and which cannot
                 * see a tag retired after this form was opened. Runs before the
                 * `previousValues` return, because tags are assigned on the
                 * edit path, which is the one that returns there.
                 */
                await assertNoRetiredTags({
                    references: values.tags,
                    tagPath: stream.tagCollection,
                    dataSource: context.dataSource,
                    locale: currentCmsLocale(),
                    message: t.retiredTags,
                });

                // A record that has never been saved has no previous values at
                // all — so this is the create path, and the only one allowed to
                // write `slug`.
                if (previousValues) return values;
                // Unreachable with `customId: true`, which will not save
                // without an id; returning rather than throwing keeps the
                // message below about the thing an editor can actually fix.
                if (!entityId) return values;
                if (!/^[a-z0-9-]+$/.test(entityId)) {
                    throw new Error(streams[stream.key].article.invalidSlug(entityId));
                }
                return { ...values, slug: entityId };
            },
            /**
             * These records are prerendered pages, so a save is invisible until
             * the site rebuilds. See `lib/rebuild.ts`.
             */
            onSaveSuccess: onSaveRebuild,
        },
        /**
         * **This list is what keeps `published` out of the form body**, and
         * that is its whole purpose: the switch is rendered in the entity
         * form's right-hand column instead, by `publishedSideSwitchPlugin`.
         * `getFormFieldKeys` returns `propertiesOrder` verbatim, so a key
         * omitted here gets no field — and FireCMS offers no form-only
         * equivalent (`disabled: { hidden: true }` would hide the field *and*
         * drop the table column).
         *
         * Two consequences worth knowing before editing it:
         *
         * - **The same list orders the collection table.** `useColumnIds` reads
         *   it too, and appends any property it does not mention — so
         *   `published` keeps its column, at the end of the row rather than
         *   between `slug` and `tags`. That is the price of the move.
         * - **Every other key has to stay listed.** An omitted key is a field
         *   an editor cannot fill in, and `title` / `body` are required — so
         *   dropping one by accident makes the form unsaveable rather than
         *   merely tidier. Adding a property means adding it here.
         */
        propertiesOrder: [
            "date",
            "updatedAt",
            "slug",
            "tags",
            ...(hasProduct ? (["product"] as const) : []),
            "img",
            "title",
            "description",
            "body",
        ],
        // Order is the toolbar's, left to right: creating an article is the
        // thing an editor came for; the rebuild buttons are a recovery path.
        Actions: [NewArticleAction, RebuildActions],
        properties: {
            date: {
                dataType: "string",
                name: t.fields.date.name,
                description: t.fields.date.description,
                defaultValue: todayIso(),
                validation: { required: true, matches: /^\d{4}-\d{2}-\d{2}$/ },
            },
            updatedAt: {
                dataType: "date",
                name: t.fields.updatedAt.name,
                description: t.fields.updatedAt.description,
                autoValue: "on_update",
                readOnly: true,
            },
            /**
             * Always read-only, and always a mirror of the document id — which
             * `NewArticleAction` assigns, so as of 2026-09-07 a slug is typed
             * nowhere at all. An indexed URL that moves is a 404, and a static
             * export has no server to redirect it, so there is no state in
             * which editing this is the right thing to do.
             *
             * It stays a real property because it is a real stored field:
             * `ArticleSchema.slug` is what the public site reads, and
             * `PropertiesOrBuilders` is a required mapped type, so it could not
             * be dropped even if the data allowed it. `onPreSave` fills it.
             *
             * Hidden from the table because the ID column already shows it.
             *
             * **No `validation` block, deliberately.** `getYupEntitySchema`
             * exempts nothing for being read-only, so a `required` rule here
             * would fail every new article — the field is empty until
             * `onPreSave` fills it, and no editor could type into it to
             * recover. The id is what gets validated, in `onPreSave`, exactly
             * as `tagCollection` does it — and the action fills both.
             */
            slug: {
                dataType: "string",
                name: t.fields.slug.name,
                description: t.fields.slug.description,
                readOnly: true,
                hideFromCollection: true,
            },
            published: {
                dataType: "boolean",
                name: t.fields.published.name,
                description: t.fields.published.description,
                defaultValue: false,
                validation: { required: true },
                // Green when live. See StatusSwitchField for why this is a custom
                // field rather than a CSS override.
                Field: StatusSwitchField,
                customProps: {
                    tone: "positive",
                    onLabel: t.fields.published.onLabel,
                    offLabel: t.fields.published.offLabel,
                },
            },
            /**
             * Chips, not FireCMS's reference cards — see `TagChipsField` for
             * the two reasons: an `EntityPreview` card per tag is ~60px of
             * form each, and each one carried a link that opened the *tag*
             * for editing. Editing a tag from an article is the wrong place
             * for it: a label edit rewrites that chip on every article
             * carrying it. The Tags collection is where that happens.
             *
             * The `of` block is unchanged and still the only place the
             * picker's rules are stated — the field reads `path` and
             * `forceFilter` off it.
             */
            tags: {
                dataType: "array",
                name: t.fields.tags.name,
                description: t.fields.tags.description,
                Field: TagChipsField,
                customProps: tagPicker,
                of: {
                    dataType: "reference",
                    path: stream.tagCollection,
                    // Retirement has to hold in the picker too, or an editor can
                    // apply a tag that renders nowhere on the public site.
                    forceFilter: { retired: ["==", false] },
                },
            },
            /**
             * A dropdown over `products`, labelled with each site's own name
             * from `org/sites.ts` — untranslated, being a product name. The
             * field's `name` / `description` are localised like every other.
             *
             * **No `validation: { required: true }`, deliberately.** The label
             * is optional, and every article written before it existed lacks
             * it — a required rule would paint each of them with a validation
             * error the next time it is opened. Clearing the dropdown writes
             * `null`, which `ArticleSchema.product`'s `blankable` reads back as
             * "no product".
             */
            ...(hasProduct && {
                product: {
                    dataType: "string",
                    name: t.fields.product.name,
                    description: t.fields.product.description,
                    enumValues: products.map((id) => ({
                        id,
                        label: SITES[id].name,
                    })),
                    nullable: true,
                },
            }),
            /**
             * A URL string with an upload button beside it.
             *
             * Not `StringProperty.storage`: FireCMS's storage field replaces the
             * text input with a dropzone, and an article about a partner may
             * legitimately point at an image we do not host. `ImageUploadField`
             * keeps both, and the stored value is a plain URL either way — so
             * `ArticleSchema.img` is untouched and no `StorageSource` is
             * involved. See `lib/uploadImage.ts` for where the bytes go.
             */
            img: {
                dataType: "string",
                name: t.fields.img.name,
                description: t.fields.img.description,
                url: true,
                Field: ImageUploadField,
                customProps: upload,
            },
            title: {
                dataType: "map",
                name: t.fields.title.name,
                expanded: true,
                properties: {
                    zh: {
                        dataType: "string",
                        name: "繁體中文",
                        validation: { required: true },
                    },
                    jp: {
                        dataType: "string",
                        name: "日本語",
                        validation: { required: true },
                    },
                },
            },
            description: {
                dataType: "map",
                name: t.fields.description.name,
                description: t.fields.description.description,
                expanded: true,
                // Prose does not fit a table cell, and a per-locale map renders
                // as an unreadable blob of both languages. `hideFromCollection`
                // is the right lever rather than `propertiesOrder`, which
                // `getFormFieldKeys` also reads — omitting a key there would
                // remove the field from the editor too.
                hideFromCollection: true,
                /**
                 * **Optional since 2026-09-07, by request — and optional as a
                 * whole, not per locale.** No `validation` on either member, so
                 * FireCMS accepts a blank; leaving one locale filled and the
                 * other empty is allowed by the form and handled by the schema,
                 * which keeps the pair a complete `LocalizedText` with `""` for
                 * the empty side. The page then ships no meta description in
                 * that locale rather than an empty one — see
                 * `blankableLocalizedText` in `packages/shared/cms/schema.ts`.
                 *
                 * **`Field: PlainTextAreaField` is not decoration.** The stock
                 * multiline field cancels IME composition on every keystroke —
                 * typing 注音 in Zhuyin produced ㄓㄨˋㄧㄣ. Removing it brings
                 * that back; the mechanism is documented on the component.
                 */
                properties: {
                    zh: {
                        dataType: "string",
                        name: "繁體中文",
                        multiline: true,
                        Field: PlainTextAreaField,
                    },
                    jp: {
                        dataType: "string",
                        name: "日本語",
                        multiline: true,
                        Field: PlainTextAreaField,
                    },
                },
            },
            /**
             * HTML, authored in Lexical. Not markdown, and not FireCMS's
             * `markdown: true` editor — see `fields/RichTextField.tsx` for why
             * it changed, and `components/editor/html.ts` for the tag
             * vocabulary this has to keep in step with the site's sanitiser.
             */
            body: {
                dataType: "map",
                name: t.fields.body.name,
                description: t.fields.body.description,
                expanded: true,
                // See `description` above.
                hideFromCollection: true,
                properties: {
                    zh: {
                        dataType: "string",
                        name: "繁體中文",
                        Field: RichTextField,
                        customProps: richText,
                        validation: { required: true },
                    },
                    jp: {
                        dataType: "string",
                        name: "日本語",
                        Field: RichTextField,
                        customProps: richText,
                        validation: { required: true },
                    },
                },
            },
        },
    });
};
